import { useState, useEffect, useCallback } from 'react';
import { useOnlineStatus } from './useOnlineStatus';
import { queueHelpers } from '@lib/offline-storage';
import { syncEngine } from '@lib/sync-engine';

/**
 * Hook para manejar sincronización de operaciones offline
 */
export function useOfflineSync() {
  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Actualizar contador de operaciones pendientes
  const updatePendingCount = useCallback(async () => {
    const operations = await queueHelpers.getAll();
    setPendingCount(operations.length);
  }, []);

  // Sincronizar operaciones pendientes
  const syncPendingOperations = useCallback(async () => {
    if (!isOnline || isSyncing) return;

    const operations = await queueHelpers.getAll();
    if (operations.length === 0) return;

    setIsSyncing(true);
    setSyncError(null);

    try {
      const result = await syncEngine.syncAll();

      if (!result.success) {
        setSyncError(
          `${result.failed} ${result.failed === 1 ? 'operación falló' : 'operaciones fallaron'} al sincronizar`
        );
      }

      await updatePendingCount();
    } catch (error) {
      console.error('Sync error:', error);
      setSyncError('Error al sincronizar operaciones pendientes');
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, updatePendingCount]);

  // Sync automático cuando vuelve la conexión
  useEffect(() => {
    if (isOnline) {
      syncPendingOperations();
    }
  }, [isOnline, syncPendingOperations]);

  // Actualizar contador al montar
  useEffect(() => {
    updatePendingCount();
  }, [updatePendingCount]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    syncError,
    syncNow: syncPendingOperations,
    refreshCount: updatePendingCount,
  };
}
